import { useEffect, useState } from 'react';
import { Accordion } from '@finn-auto/design-system/atoms/accordion';
import type { FeedbackItem, RoleBoard } from '../../types/taActionBoard';
import { FeedbackCard, type FeedbackAction } from './CandidateCard';
import { COLORS } from './format';
import { CancelledSection } from './CancellationTag';
import { FilterChip, RoleAccordion } from './RoleAccordion';
import { RolePipeline } from './RolePipeline';
import { ScheduledSendsStrip } from './ScheduledSendsStrip';
import type { FocusTarget } from './useFocusCard';


/** The outcome the summary chips filter by (null = show everything). */
type DecisionFilter = 'hire' | 'review' | 'no-hire';

/**
 * The `handled` label the Discard button writes (via markHandled in the parent).
 *
 * Unlike "Archived" / "Moved to …", a discarded card is taken OFF the overview
 * entirely rather than left muted: it is not a decision about the candidate, so
 * there is nothing to show the recruiter afterwards. Reusing `handled` means it
 * already drops out of the chip counts and persists in the board cache until the
 * next fresh Lever pull, with no new state to thread through.
 */
export const DISCARDED_LABEL = 'Discarded';

interface FeedbackTabProps {
  roles: RoleBoard[];
  /** Whose board is on screen — the shared pipeline strip's Lever retry needs it. */
  boardEmail: string;
  /** Reload the board after that retry. */
  onChanged?: () => void;
  /** opportunityId → "Archived" / "Moved to …" — cards render muted. */
  handled: Record<string, string>;
  focus: FocusTarget | null;
  onClearFocus: () => void;
  onAction: (role: RoleBoard, action: FeedbackAction) => void;
}

/** 📝 Interview feedback — decisions waiting on the recruiter. */
export function FeedbackTab({
  roles,
  boardEmail,
  onChanged,
  handled,
  focus,
  onClearFocus,
  onAction,
}: FeedbackTabProps) {
  const firstOpen = roles.find((r) => r.feedback.length > 0)?.postingId;
  const [userOpen, setUserOpen] = useState<string[]>(firstOpen ? [firstOpen] : []);
  // Click an outcome chip to show only those cards (still grouped per role); click the
  // active chip again to clear. Counts on the chips stay the totals either way.
  const [filter, setFilter] = useState<DecisionFilter | null>(null);
  const toggle = (f: DecisionFilter) => setFilter((cur) => (cur === f ? null : f));

  const items = roles.flatMap((r) => r.feedback).filter((i) => !handled[i.opportunityId]);
  const moves = items.filter((i) => i.decision === 'hire').length;
  const archives = items.filter((i) => i.decision === 'no-hire').length;
  // A split rating ('review') is its own outcome — a hire needs both interviewers on
  // board — so it gets its own bucket instead of being counted as neither a move nor
  // an archive (which quietly under-reported the work still needing a human call).
  const toReview = items.filter((i) => i.decision === 'review').length;

  // Per role, the feedback left after the active filter. While a filter is on, roles
  // with nothing matching drop out entirely rather than showing an empty accordion.
  // Discarded cards are removed outright (archived/moved cards stay, muted) — the point
  // of Discard is to take the card off the overview, not grey it out in place.
  const perRole = roles.map((role) => ({
    role,
    shown: (filter ? role.feedback.filter((i) => i.decision === filter) : role.feedback).filter(
      (i) => handled[i.opportunityId] !== DISCARDED_LABEL
    ),
  }));
  const visibleRoles = filter ? perRole.filter(({ shown }) => shown.length > 0) : perRole;

  // With a filter on, expand every matching role so the cards are visible without
  // extra clicks; otherwise honour the recruiter's own toggles (+ the SLA "go to
  // decision" focus, which expands its target without touching those toggles).
  const open = filter
    ? visibleRoles.map(({ role }) => role.postingId)
    : focus && !userOpen.includes(focus.postingId)
      ? [...userOpen, focus.postingId]
      : userOpen;

  // Scroll the highlighted candidate into view once the accordion has expanded.
  useEffect(() => {
    if (!focus) return;
    const t = setTimeout(() => {
      document
        .getElementById(`ta-card-${focus.opportunityId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => clearTimeout(t);
  }, [focus]);

  /**
   * Cancelled interviews with no scheduling section of their own.
   *
   * ABOVE the decision chips, not inside a role accordion: these are the
   * candidates most likely to be forgotten — the board showed them nowhere at
   * all until now — and burying them one expand deep would repeat the original
   * failure in a smaller way. Never filtered by the decision chips either;
   * "to advance / to reject" is about feedback, and a cancelled interview
   * produced none.
   */
  const cancelled = roles.flatMap((r) => r.cancelled ?? []);

  return (
    <div className="space-y-4">
      {/* A rejection queued from an archive is the one thing on this tab that will send on its own —
          so it gets the same visible, cancellable strip that queued replies get on Requests. */}
      <ScheduledSendsStrip kind="rejection" />
      <CancelledSection candidates={cancelled} />
      <div className="flex flex-wrap gap-2.5">
        <FilterChip label="All" count={items.length} active={filter === null} onClick={() => setFilter(null)} />
        <FilterChip
          label="To advance"
          count={moves}
          dot={COLORS.statusGreen}
          active={filter === 'hire'}
          onClick={() => toggle('hire')}
        />
        <FilterChip
          label="Mixed feedback"
          count={toReview}
          dot={COLORS.statusOrange}
          active={filter === 'review'}
          onClick={() => toggle('review')}
        />
        <FilterChip
          label="To reject"
          count={archives}
          dot={COLORS.statusRed}
          active={filter === 'no-hire'}
          onClick={() => toggle('no-hire')}
        />
      </div>

      <Accordion
        type="multiple"
        value={open}
        onValueChange={(v: string[]) => {
          setUserOpen(v);
          onClearFocus();
        }}
        className="space-y-3"
      >
        {visibleRoles.map(({ role, shown }) => (
          <RoleAccordion
            key={role.postingId}
            role={role}
            hasOpenItems={shown.length > 0}
            chip={{
              label: `${shown.length} decision${shown.length === 1 ? '' : 's'}`,
              bg: COLORS.blueBg,
              text: COLORS.blueText,
            }}
          >
            <div className="space-y-3">
              <RoleFeedbackList
                role={role}
                items={shown}
                handled={handled}
                highlightId={focus?.postingId === role.postingId ? focus.opportunityId : null}
                onAction={onAction}
              />
              {/*
                This card is defined as "submitted feedback awaiting a decision", so a role
                with nine people mid-interview and one written-up round reads as a role with
                one candidate. The strip is the rest of the picture (Theresa, Oct 2026).
              */}
              <RolePipeline role={role} boardEmail={boardEmail} onChanged={onChanged} />
            </div>
          </RoleAccordion>
        ))}
      </Accordion>
    </div>
  );
}

function RoleFeedbackList({
  role,
  items,
  handled,
  highlightId,
  onAction,
}: {
  role: RoleBoard;
  /** Already filtered by the active outcome chip (all of the role's feedback when none). */
  items: FeedbackItem[];
  handled: Record<string, string>;
  highlightId: string | null;
  onAction: (role: RoleBoard, action: FeedbackAction) => void;
}) {
  if (items.length === 0) {
    return <p className="text-sm text-gray-300">Nothing open — no submitted feedback waiting.</p>;
  }
  return (
    <div className="space-y-3 rounded-lg p-3" style={{ backgroundColor: COLORS.cotton }}>
      {items.map((item: FeedbackItem) => (
        <FeedbackCard
          key={item.opportunityId}
          item={item}
          handledLabel={handled[item.opportunityId] || null}
          highlighted={highlightId === item.opportunityId}
          onAction={(action) => onAction(role, action)}
        />
      ))}
    </div>
  );
}
